import type { ISetting, ISettingPreset } from '@newo/api/gateway';
import yup from '@/libs/yup';

type IRecord = Record<string, unknown>;

export const isSettingVisible = (
    preset: ISettingPreset,
    values: IRecord
): boolean => {
    const dependsOn = preset.depends_on;

    if (!dependsOn) {
        return true;
    }

    return dependsOn.values.includes(
        values[dependsOn.setting_idn] as string | number
    );
};

export const pickVisibleSettings = (
    presets: ISettingPreset[],
    values: IRecord
): IRecord => {
    const visibleIdns = new Set(
        presets
            .filter((preset) => isSettingVisible(preset, values))
            .map(({ idn }) => idn)
    );

    return Object.fromEntries(
        Object.entries(values).filter(([idn]) => visibleIdns.has(idn))
    );
};

export const buildFormFromPreset = (
    presets: ISettingPreset[],
    settings: ISetting[] = []
) => {
    const settingsHash: IRecord = Object.fromEntries(
        settings.map(({ idn, value }) => [idn, value])
    );
    const initialValues: IRecord = Object.fromEntries(
        presets.map(({ idn }) => [idn, settingsHash[idn] ?? ''])
    );

    const validationSchema: yup.ObjectSchema<IRecord> = yup.object(
        presets.reduce((hash, itm) => {
            let schema = yup.string().trim();
            const dependsOn = itm.depends_on;
            if (itm.is_required) {
                schema = dependsOn
                    ? schema.when(dependsOn.setting_idn, {
                          is: (value: unknown) =>
                              dependsOn.values.includes(
                                  value as string | number
                              ),
                          then: (conditionalSchema) =>
                              conditionalSchema.required(
                                  `${itm.title} is required`
                              )
                      })
                    : schema.required(`${itm.title} is required`);
            }
            // TODO: remove after integrations refactoring https://newo.youtrack.cloud/issue/NPE-4583
            if (itm.idn === 'client_website_url') {
                schema = schema.urlList();
            }
            return {
                ...hash,
                [itm.idn]: schema
            };
        }, {})
    );

    return {
        initialValues,
        validationSchema
    };
};

export const getRequiredSettings = (
    preset: ISettingPreset[],
    settings: ISetting[]
) => {
    return preset.filter((setting) => {
        const settingValue = settings.find((s) => s.idn === setting.idn);
        if (
            setting.value_type === 'external_sensitive_value' &&
            setting.default_value !== null
        ) {
            return false;
        }
        return !settingValue?.value;
    });
};

export const valuesToSettings = (
    values: Record<string, unknown>
): ISetting[] => {
    return Object.entries(values).map(([idn, value]) => ({
        idn,
        value
    }));
};
